import {
  AADetectionResults,
  IframeAction,
  SoftwallRecoveredType,
  StorageType,
} from "../types";
import {
  AllowlistState,
  DAY_IN_MILLISECONDS,
  getExpiryTimeFromDays,
  setServerSideUserState,
} from "../utils";
import { deleteRenderData } from "./render";
import { setAttributionExpiry } from "./attributionExpiry";
import { getSingleClickFlag } from "./singleClick";
import { getIframeData, updateStorageData } from "./iframe";
import { safeLocalStorage } from "./storage";
import { getUserState } from "./common";

const BT_AM_SOFTWALL_RECOVERED = "BT_AM_SOFTWALL_RECOVERED";

export async function hasRecoveryData(): Promise<boolean> {
  if (safeLocalStorage.get(BT_AM_SOFTWALL_RECOVERED)) {
    return true;
  }
  return getIframeData(StorageType.Local, BT_AM_SOFTWALL_RECOVERED)
    .then((iframeData) => {
      if (iframeData) {
        // set data to local storage so on next page load
        // it will be available without need to post message to iframe
        safeLocalStorage.set(BT_AM_SOFTWALL_RECOVERED, iframeData);
        return true;
      }
      return false;
    })
    .catch(() => false);
}

export async function validateRecovery(
  renderToAAUsersAllowed: boolean,
  attributionWindowDays: number,
  aaDetectionResults: AADetectionResults,
): Promise<{
  swRecovered: SoftwallRecoveredType | undefined;
  attributionExpiry: number | undefined;
}> {
  const attributions = await getUserState();
  // if no data in local storage, that mean that wall has never been rendered for this user
  if (!attributions)
    return { swRecovered: undefined, attributionExpiry: undefined };

  const lastRenderDate = attributions.renderDate;
  const attributionExpiry = attributions.attributionExpiry;
  const latestRecoveryState = attributions.recoveryType;

  let currentRecoveryState: SoftwallRecoveredType | undefined;
  if (!aaDetectionResults.ab) {
    currentRecoveryState = "AB";
  } else if (aaDetectionResults.aa && !renderToAAUsersAllowed) {
    currentRecoveryState = "AA";
  }

  const validatedExpiry =
    attributionExpiry ||
    getExpiryTimeFromDays(attributionWindowDays, lastRenderDate?.getTime());
  const isWithingRecoveryWindow = isWithinTheRecoveryWindow(lastRenderDate);

  // Handle AA-to-AB recovery transition:
  // Only apply if within the recovery window
  if (latestRecoveryState === "AA" && currentRecoveryState === "AB") {
    if (isWithingRecoveryWindow) {
      // persist the updated recovery type in storage
      saveRecovery(currentRecoveryState);
    } else {
      // fallback to the latest recovery state
      return {
        swRecovered: latestRecoveryState,
        attributionExpiry: validatedExpiry,
      };
    }
  }

  // if user was recovered, but turn on AB again or disable the AA or
  // if MW has been rendered outside the recovery window and user still has AB enabled
  // allow MW to be rendered again and invalidate previous render data
  if (
    (latestRecoveryState || !isWithingRecoveryWindow) &&
    currentRecoveryState === undefined // if user no longer meets the rules to be identified as recovered - the currentRecoveryState value will be undefined
  ) {
    deleteRenderData();
  } else if (latestRecoveryState) {
    // update expiry if user still recovered but state incomplete
    if (!attributionExpiry) {
      setAttributionExpiry(validatedExpiry);
    }
  }

  if (!latestRecoveryState && !attributionExpiry && !isWithingRecoveryWindow)
    return { swRecovered: undefined, attributionExpiry: undefined };

  // return actual user's recovery state
  return {
    swRecovered: currentRecoveryState,
    attributionExpiry: validatedExpiry,
  };
}

export function ensureRecoveryType(
  swRecovered: SoftwallRecoveredType | undefined,
  allowlistState?: AllowlistState,
): SoftwallRecoveredType | undefined {
  if (!swRecovered) return undefined;

  // check if user SCA recovered
  // on SCA we expect AB recovery type, since website should be allowlisted
  if (swRecovered === "AB") {
    // Flag stored in localstorage presented is SCA event pass successfully
    // set in false if SCA failed on timeout or because of some error on the extension side.
    // ExtensionInfo allowlistState contain status and source properties to highlight if website was allowlisted via SCA event
    const scaFlag = getSingleClickFlag();
    const scaState = allowlistState?.status && allowlistState?.source === "1ca";
    return scaState || scaFlag ? "SCA" : swRecovered;
  }

  return swRecovered;
}

export async function getRecoveryData(): Promise<
  SoftwallRecoveredType | undefined
> {
  // if window.__bt_intrnl.recoveryUserAttr defined it means that
  // bt_tag or bt_am already tried to restore server side user attribution in current page view,
  // so the recovery type value could be getting from the window object;
  if (window.__bt_intrnl.recoveryUserAttr) {
    return window.__bt_intrnl.recoveryUserAttr.recoveryType;
  }

  const recoveryStr = safeLocalStorage.get(BT_AM_SOFTWALL_RECOVERED);
  if (recoveryStr) {
    return recoveryStr as SoftwallRecoveredType;
  }

  return getIframeData(StorageType.Local, BT_AM_SOFTWALL_RECOVERED)
    .then((iframeData) => {
      if (iframeData) {
        // set data to local storage so on next page load
        // it will be available without need to post message to iframe
        safeLocalStorage.set(BT_AM_SOFTWALL_RECOVERED, iframeData);
        return iframeData as SoftwallRecoveredType;
      }
      return undefined;
    })
    .catch(() => undefined);
}

export const saveRecoveryData = async (
  recoveredData: SoftwallRecoveredType,
  attributionExpiry: number,
): Promise<void> => {
  return Promise.all([
    saveRecovery(recoveredData),
    setAttributionExpiry(attributionExpiry),
    setServerSideUserState(recoveredData, attributionExpiry),
  ]).then(() => null);
};

export const saveRecovery = (recoveredData: SoftwallRecoveredType) =>
  updateStorageData(
    IframeAction.Set,
    StorageType.Local,
    BT_AM_SOFTWALL_RECOVERED,
    recoveredData,
  );

export const deleteRecoveryData = () =>
  updateStorageData(
    IframeAction.Delete,
    StorageType.Local,
    BT_AM_SOFTWALL_RECOVERED,
  );

const MAX_DAYS_TO_RECOVER = 7;

/*
 * Check if the last render is inside the time limit
 * Otherwise it means that the user has recovered but not due to the softwall
 * */
const isWithinTheRecoveryWindow = (lastRenderDate: Date | undefined) =>
  lastRenderDate
    ? differenceInDays(lastRenderDate) <=
      MAX_DAYS_TO_RECOVER * DAY_IN_MILLISECONDS
    : false;

const differenceInDays = (date1: Date, date2: Date = new Date()) =>
  Math.abs(date1.getTime() - date2.getTime());
